import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { serialApi, SerialDetailResponse } from '@/api/serial.api';
import AppLayout from '@/components/feature/AppLayout';

export default function SerialTraceabilityPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState(searchParams.get('sn') || '');
  const [result, setResult] = useState<SerialDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initialQuery = searchParams.get('sn');
    if (initialQuery) {
      setQuery(initialQuery);
      performSearch(initialQuery);
    }
  }, [searchParams]);

  const performSearch = async (searchTerm: string) => {
    const term = searchTerm.trim();
    if (!term) return;

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const res = await serialApi.trace(term);
      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setError(`No serial record found for "${term}".`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || `No serial found matching "${term}".`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(query);
  };

  const serial = result?.serial;
  const movements = result?.movements || [];

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      {/* Top Banner */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/inventory/serials')}
            className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i> Back to Serial Numbers
          </button>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-gradient-to-tr from-indigo-600 to-purple-600 text-white rounded-xl shadow-sm">
              <i className="ri-route-line text-xl"></i>
            </span>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Serial Traceability Journey</h1>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Search any Serial Number, Vendor Service Tag, or IMEI to view its full end-to-end voyage.
          </p>
        </div>
      </div>

      {/* Search Input Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <form onSubmit={handleSearchSubmit} className="flex gap-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
              <i className="ri-search-2-line text-lg"></i>
            </div>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter Serial Number / Vendor Serial / IMEI (e.g. SN-LAP-2026-0001)..."
              className="w-full pl-11 pr-4 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
            />
          </div>
          <button
            type="submit"
            disabled={!query.trim() || loading}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-md flex items-center gap-2 transition-all"
          >
            {loading ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-radar-line text-base"></i>
            )}
            Trace Journey
          </button>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Traceability Result View */}
      {serial && (
        <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
          {/* Header Card */}
          <div className="bg-gradient-to-r from-gray-900 to-indigo-950 text-white p-6 rounded-2xl shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <span className="text-xs uppercase tracking-wider text-indigo-300 font-semibold">Identified Unit</span>
              <h2 className="text-2xl font-bold font-mono text-white mt-0.5">{serial.serial_number}</h2>
              <p className="text-xs text-indigo-200 mt-1">
                Item: <strong className="text-white">{serial.item_name}</strong> {serial.item_code ? `(${serial.item_code})` : ''} • Vendor Tag: <span className="font-mono text-amber-300">{serial.vendor_serial_number || 'None'}</span>
              </p>
            </div>

            <div className="flex flex-col items-start sm:items-end gap-1">
              <span className="text-xs text-indigo-300">Current Status</span>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                {serial.status} ({serial.condition})
              </span>
            </div>
          </div>

          {/* Visual Step-by-Step Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Step 1: PURCHASE */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-2 h-full bg-emerald-500"></div>
              <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                <i className="ri-inbox-archive-line text-lg"></i>
                <span>1. PURCHASE & INWARD</span>
              </div>
              <div className="space-y-1.5 text-xs text-gray-700">
                <div><span className="text-gray-400">Vendor:</span> <strong className="text-gray-900">{serial.supplier_name || 'Vendor Inward'}</strong></div>
                <div><span className="text-gray-400">GRN No:</span> <span className="font-mono font-bold text-emerald-700">{serial.grn_number || 'Direct Entry'}</span></div>
                {serial.po_number && <div><span className="text-gray-400">PO No:</span> <span className="font-mono">{serial.po_number}</span></div>}
                <div><span className="text-gray-400">Inward Date:</span> {serial.grn_date || '—'}</div>
                <div><span className="text-gray-400">Purchase Rate:</span> ₹{Number(serial.purchase_rate || 0).toLocaleString('en-IN')}</div>
              </div>
            </div>

            {/* Step 2: STORAGE */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-2 h-full bg-blue-500"></div>
              <div className="flex items-center gap-2 text-blue-600 font-bold text-sm">
                <i className="ri-building-line text-lg"></i>
                <span>2. WAREHOUSE STORAGE</span>
              </div>
              <div className="space-y-1.5 text-xs text-gray-700">
                <div><span className="text-gray-400">Warehouse:</span> <strong className="text-gray-900">{serial.warehouse_name || 'Main Warehouse'}</strong></div>
                <div><span className="text-gray-400">Shelf Location:</span> {serial.shelf_location || 'General Staging'}</div>
                <div><span className="text-gray-400">Condition:</span> <span className="font-semibold text-blue-700">{serial.condition}</span></div>
                <div><span className="text-gray-400">Warranty Till:</span> {serial.warranty_expiry_date || 'None'}</div>
              </div>
            </div>

            {/* Step 3: SALE */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3 relative overflow-hidden">
              <div className={`absolute top-0 right-0 w-2 h-full ${serial.status === 'SOLD' ? 'bg-indigo-500' : 'bg-gray-300'}`}></div>
              <div className="flex items-center gap-2 text-indigo-600 font-bold text-sm">
                <i className="ri-shopping-bag-line text-lg"></i>
                <span>3. SALE OUTWARD</span>
              </div>
              {serial.invoice_number || serial.status === 'SOLD' ? (
                <div className="space-y-1.5 text-xs text-gray-700">
                  <div><span className="text-gray-400">Customer:</span> <strong className="text-gray-900">{serial.customer_name || 'Customer'}</strong></div>
                  <div><span className="text-gray-400">Invoice:</span> <span className="font-mono font-bold text-indigo-700">{serial.invoice_number}</span></div>
                  <div><span className="text-gray-400">Sale Date:</span> {serial.invoice_date || '—'}</div>
                  <div><span className="text-gray-400">Sale Rate:</span> ₹{Number(serial.sale_rate || 0).toLocaleString('en-IN')}</div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Item has not been sold yet.</p>
              )}
            </div>

            {/* Step 4: RETURN / SERVICE */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3 relative overflow-hidden">
              <div className={`absolute top-0 right-0 w-2 h-full ${serial.status === 'RETURNED' || serial.status === 'IN_REPAIR' || serial.status === 'DEFECTIVE' ? 'bg-amber-500' : 'bg-gray-300'}`}></div>
              <div className="flex items-center gap-2 text-amber-600 font-bold text-sm">
                <i className="ri-arrow-go-back-line text-lg"></i>
                <span>4. RMA & SERVICE STATUS</span>
              </div>
              {serial.status === 'RETURNED' || serial.status === 'IN_REPAIR' || serial.status === 'DEFECTIVE' ? (
                <div className="space-y-1.5 text-xs text-gray-700">
                  <div><span className="text-gray-400">Current Phase:</span> <strong className="text-amber-800 font-bold">{serial.status}</strong></div>
                  <div><span className="text-gray-400">Condition:</span> <span className="text-rose-600 font-semibold">{serial.condition}</span></div>
                  {serial.notes && <div><span className="text-gray-400">Notes:</span> {serial.notes}</div>}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No return or repair recorded for this item.</p>
              )}
            </div>
          </div>

          {/* Full Audit Log */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <i className="ri-time-line text-gray-500"></i> Event Audit Trail ({movements.length} steps)
            </h3>
            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {movements.map((m, idx) => (
                <div key={m.id} className="p-3.5 flex flex-col sm:flex-row justify-between items-start sm:items-center text-xs hover:bg-gray-50">
                  <div className="flex items-center gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-mono text-[10px] font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div>
                      <span className="font-bold text-gray-900 font-mono">[{m.movement_type}] {m.reference_type}</span>
                      {m.notes && <span className="text-gray-500 ml-2 font-normal">— {m.notes}</span>}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-400 font-mono mt-1 sm:mt-0">
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </AppLayout>
  );
}
